import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { MotionConfig } from 'motion/react';
import { Router } from 'react-router-dom';
import { renderRoutes, RouteConfig } from 'react-router-config';
import 'common/helpers/featureToggle';
import { AnyAction, ReducersMapObject } from 'redux';
import { configureUiKit } from '@stripcash/ui-kit';
import Theme from '@stripcash/ui-kit/hoc/Theme';
import { Middleware } from '@reduxjs/toolkit';
import { Fragment, StrictMode } from 'react';

import initAnalytics from 'common/helpers/initAnalytics';
import { APP_INIT, fetchConfig } from 'common/actions/config';
import Trans from 'common/i18n/Trans';
import configureStore from 'common/apps/core/configureStore';
import { SCROLL_CONTAINER_ID } from 'common/constants/ui';
import ShortcutsListener from 'common/layouts/ShortcutsListener';
import Z_INDEXES from 'common/constants/zIndexes';
import onThemeChange from 'common/helpers/onThemeChange';
import getInitialTheme from 'common/helpers/getInitialTheme';

import './styles.css';

const root = createRoot(document.getElementById('app')!);

configureUiKit({
  scrollContainerId: SCROLL_CONTAINER_ID,
  basePopoverZIndex: Z_INDEXES.header,
});

initAnalytics();

const render = <S extends any>({
  routes,
  reducer,
  middleware = [],
}: {
  routes: RouteConfig[];
  reducer: ReducersMapObject<S, AnyAction>;
  middleware?: Middleware<S>[];
}) => {
  const StrictWrapper = BUILD_ENV === 'development' ? StrictMode : Fragment;
  const reducedMotion = document.cookie.includes('env=autotest');
  const { store, history } = configureStore(reducer, middleware);

  store.dispatch({ type: APP_INIT });

  return store
    .dispatch(fetchConfig() as unknown as AnyAction)
    .then(() => {
      root.render(
        <StrictWrapper>
          <Provider store={store}>
            <Trans>
              <ShortcutsListener>
                <MotionConfig reducedMotion={reducedMotion ? 'always' : 'user'}>
                  <Theme
                    onThemeChange={onThemeChange}
                    getInitialTheme={getInitialTheme}
                  >
                    <Router history={history}>{renderRoutes(routes)}</Router>
                  </Theme>
                </MotionConfig>
              </ShortcutsListener>
            </Trans>
          </Provider>
        </StrictWrapper>,
      );
    })
    .catch((error) => {
      console.log(error);
    });
};

export default render;
